import React, { useState, useEffect } from 'react';
import Dialog from '../Dialog/Dialog';
import Input from '../Input/Input';
import { TextButton } from '../Button';
import { useProfile } from '../../context/ProfileContext';
import './ProfileDialog.css';

interface ProfileDialogProps {
  isOpen: boolean;
  onClose: (updated?: boolean) => void;
}

const ProfileDialog: React.FC<ProfileDialogProps> = ({ isOpen, onClose }) => {
  const { profileData, isLoading: profileLoading, updateProfile } = useProfile();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string; email?: string }>({});

  useEffect(() => {
    // Initialize form with existing profile data when available
    if (isOpen && profileData) {
      setFirstName(profileData.first_name || '');
      setLastName(profileData.last_name || '');
      setEmail(profileData.email || '');
      setErrors({});
    }
  }, [isOpen, profileData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const newErrors: { firstName?: string; lastName?: string; email?: string } = {};
    
    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsLoading(true);
    
    try {
      const success = await updateProfile({
        first_name: firstName,
        last_name: lastName,
        email: email
      });
      
      if (!success) {
        throw new Error('Failed to update profile');
      }
      
      // Close the dialog and indicate success
      onClose(true);
    } catch (err) {
      setErrors({ ...errors, firstName: 'Failed to update profile. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const headerActions = (
    <div className="dialog-header-actions">
      <TextButton 
        variant="secondary" 
        onClick={() => onClose(false)}
        disabled={isLoading || profileLoading}
        size="small"
      >
        Cancel
      </TextButton>
      <TextButton 
        variant="primary" 
        onClick={handleSubmit}
        disabled={isLoading || profileLoading}
        isLoading={isLoading || profileLoading}
        size="small"
      >
        Save
      </TextButton>
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => onClose(false)}
      title="Edit Profile"
      headerActions={headerActions}
      showCloseButton={false}
    >
      <form onSubmit={handleSubmit} className="profile-form">
        <Input
          label="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Enter first name"
          error={errors.firstName}
          disabled={isLoading || profileLoading}
          required
        />
        
        <Input
          label="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Enter last name"
          error={errors.lastName}
          disabled={isLoading || profileLoading}
        />
        
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          error={errors.email}
          disabled={isLoading || profileLoading}
          required
        />
      </form>
    </Dialog>
  );
};

export default ProfileDialog; 